import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    IconSparkles, IconArrowLeft, IconDeviceFloppy, IconPlus, IconTrash, IconLoader2, IconWorld,
    IconUpload, IconFileText, IconX, IconLink, IconClipboardText, IconApi, IconDownload, IconWand,
    IconGripVertical, IconWand as IconCustom, IconPhoto, IconReceipt2, IconChevronDown,
} from '@tabler/icons-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { notifications } from '@/lib/notifications';
import PageHeader from '../../../components/common/PageHeader';
import SelectField from '../../../components/common/SelectField';
import DatePickerField from '../../../components/common/DatePickerField';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import jobService from '../../../services/jobService';

const CATEGORY_OPTS = ['government', 'defense', 'bank', 'education', 'private'];
const APPLY_OPTS = ['online', 'postal'];
const STATUS_OPTS = ['published', 'draft', 'archived'];
const ELIG_KEYS = ['edu', 'age', 'salary', 'fee', 'custom'];
const TELETALK_MAX = 50;

/** Empty string → fetch all; otherwise clamp to 1–50. */
const parseTeletalkLimit = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return null;
    return Math.min(Math.max(Number(s) || 1, 1), TELETALK_MAX);
};

const EMPTY = {
    organization: '', position: '', avatarText: '', category: 'government', secondaryTag: '',
    totalVacancies: '', gradeLabel: '', workplace: '', applyMethod: 'online', applyMethodLabel: '',
    publishedDate: null, deadlineDate: null, feePaymentNote: '', examDateNote: '',
    noticeUrl: '', applyUrl: '', logoUrl: '', status: 'published', isFeatured: false, sortOrder: 0,
    posterQualification: '', posterBosoyShima: '',
    vacancies: [], eligibility: [], process: [],
};

// Parse a 'YYYY-MM-DD' (or ISO) string into a Date, else null.
const toDate = (s) => { if (!s) return null; const d = new Date(s); return Number.isNaN(d.getTime()) ? null : d; };
// Date → 'YYYY-MM-DD' for the API.
const toYmd = (d) => { if (!d) return null; const dt = new Date(d); return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10); };

// Stable client-side id for drag-and-drop keys (never persisted — stripped on save).
const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `q_${Date.now()}_${Math.random().toString(36).slice(2)}`);
// Ensure every row in a qualification/eligibility array carries a drag id.
const withIds = (arr) => (Array.isArray(arr) ? arr : []).map((r) => ({ _id: genId(), ...r }));
// Drop the client-only drag id before sending to the API.
const stripId = ({ _id, ...rest }) => rest;

const QUAL_INPUT = 'px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40';

// A single draggable qualification/eligibility row: [grip] [type select] [custom label?] [details] [delete].
function SortableQualRow({ row, t, opt, onUpdate, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row._id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, position: 'relative', zIndex: isDragging ? 50 : 'auto' };
    return (
        <div ref={setNodeRef} style={style} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <button type="button" {...attributes} {...listeners} title={t('adminJobs.elig.reorder')}
                className="hidden sm:flex items-center px-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-grab active:cursor-grabbing"
                style={{ touchAction: 'none' }}>
                <IconGripVertical size={16} />
            </button>
            {row.key === 'custom' ? (
                <input value={row.label || ''} onChange={(e) => onUpdate(row._id, 'label', e.target.value)} placeholder={t('adminJobs.elig.customLabel')} className={`sm:w-44 ${QUAL_INPUT}`} />
            ) : (
                <div className="sm:w-44">
                    <SelectField value={row.key} onChange={(v) => onUpdate(row._id, 'key', v)} options={opt(ELIG_KEYS, 'adminJobs.elig')} />
                </div>
            )}
            <input value={row.value} onChange={(e) => onUpdate(row._id, 'value', e.target.value)} placeholder={t('adminJobs.elig.value')} className={`flex-1 ${QUAL_INPUT}`} />
            <button type="button" onClick={() => onDelete(row._id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 self-end sm:self-auto"><IconTrash size={16} /></button>
        </div>
    );
}

// Reorderable list of qualification rows + "Add" / "Add custom" buttons.
// Shared by the common eligibility card and each post's qualifications.
function QualEditor({ rows, t, opt, addLabel, onReorder, onUpdate, onDelete, onAdd, onAddCustom }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = rows.findIndex((r) => r._id === active.id);
        const newIndex = rows.findIndex((r) => r._id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(rows, oldIndex, newIndex));
    };
    return (
        <div className="space-y-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={rows.map((r) => r._id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                        {rows.map((row) => (
                            <SortableQualRow key={row._id} row={row} t={t} opt={opt} onUpdate={onUpdate} onDelete={onDelete} />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
            <div className="flex flex-wrap items-center gap-4">
                <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><IconPlus size={15} /> {addLabel}</button>
                <button type="button" onClick={onAddCustom} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><IconCustom size={15} /> {t('adminJobs.elig.addCustom')}</button>
            </div>
        </div>
    );
}

// Reusable labelled text input.
function TextInput({ label, value, onChange, placeholder, type = 'text' }) {
    return (
        <label className="block">
            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{label}</span>
            <input
                type={type}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
        </label>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-5 space-y-4">
            {title && <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{title}</h3>}
            {children}
        </div>
    );
}

// Internal accounting rate for showing AI token cost in BDT (change in one place).
const USD_TO_BDT = 125;

// Admin-only debug/cost panel: floats top-right, collapsed by default. Shows the
// last AI extraction's model, token usage and cost (USD + BDT). This whole page is
// already admin-guarded, so no extra role check is needed. Renders nothing until an
// AI analyse runs (or an edited job carries saved metadata.ai).
function AiUsagePanel({ usage }) {
    const [open, setOpen] = useState(false);
    if (!usage) return null;

    const usd = Number(usage.usd_cost || 0);
    const bdt = usd * USD_TO_BDT;
    const fmt = (n) => Number(n || 0).toLocaleString('en-US');
    const total = usage.total_tokens || (usage.prompt_tokens || 0) + (usage.completion_tokens || 0);
    const when = usage.scrapedAt ? new Date(usage.scrapedAt).toLocaleString() : null;
    const rows = [
        ['Model', usage.model || '—'],
        ['Source', usage.source || '—'],
        ['Prompt tokens', fmt(usage.prompt_tokens)],
        ['Completion tokens', fmt(usage.completion_tokens)],
        ['Reasoning tokens', fmt(usage.reasoning_tokens)],
        ['Total tokens', fmt(total)],
    ];

    return (
        <div className="fixed top-20 right-4 z-40 w-72 max-w-[calc(100vw-2rem)]">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white/95 dark:bg-zinc-900/95 backdrop-blur px-3 py-2 shadow-lg"
            >
                <span className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-200">
                    <IconReceipt2 size={15} className="text-primary" />
                    AI log · ৳{bdt.toFixed(2)}
                </span>
                <IconChevronDown size={15} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="mt-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white/95 dark:bg-zinc-900/95 backdrop-blur p-3 shadow-lg space-y-1.5">
                    <div className="flex items-baseline justify-between">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">Cost (BDT)</span>
                        <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">৳{bdt.toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">Cost (USD)</span>
                        <span className="text-xs font-mono text-zinc-600 dark:text-zinc-300">${usd.toFixed(6)}</span>
                    </div>
                    <div className="h-px bg-zinc-100 dark:bg-zinc-800 my-1" />
                    {rows.map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-2">
                            <span className="text-xs text-zinc-500 dark:text-zinc-400 shrink-0">{k}</span>
                            <span className="text-xs font-mono text-zinc-700 dark:text-zinc-200 truncate">{v}</span>
                        </div>
                    ))}
                    {usage.scrapedUrl && (
                        <div className="pt-0.5">
                            <span className="block text-xs text-zinc-500 dark:text-zinc-400">Source URL</span>
                            <a href={usage.scrapedUrl} target="_blank" rel="noreferrer" className="block text-xs text-primary truncate">{usage.scrapedUrl}</a>
                        </div>
                    )}
                    {when && <div className="text-[11px] text-zinc-400 pt-1">{when}</div>}
                    <div className="text-[10px] text-zinc-400 pt-1 mt-1 border-t border-zinc-100 dark:border-zinc-800">
                        Rate: $1 = ৳{USD_TO_BDT} · internal accounting only
                    </div>
                </div>
            )}
        </div>
    );
}

export default function JobFormPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [form, setForm] = useState(EMPTY);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    // AI auto-fill: url | upload | text | teletalk
    const [aiMode, setAiMode] = useState('url');
    const [scrapeUrl, setScrapeUrl] = useState('');
    const [pasteText, setPasteText] = useState('');
    const [files, setFiles] = useState([]);
    const [scraping, setScraping] = useState(false);
    const [aiUsage, setAiUsage] = useState(null);
    const fileInputRef = useRef(null);

    // Teletalk import mode — fetch jobs from Alljobs by Teletalk, review, bulk-import
    const [teletalkKeyword, setTeletalkKeyword] = useState('');
    const [teletalkLimit, setTeletalkLimit] = useState('');
    const [parseCircular, setParseCircular] = useState(true);
    const [teletalkResults, setTeletalkResults] = useState([]);
    const [teletalkMeta, setTeletalkMeta] = useState(null); // { total, fetched, limit }
    const [importing, setImporting] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Organization logo upload (compressed webp ≤10KB).
    const [logoUploading, setLogoUploading] = useState(false);
    const logoFileRef = useRef(null);
    const handleLogoFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setLogoUploading(true);
        try {
            const { data } = await jobService.uploadLogo(file);
            set('logoUrl', data.url);
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error?.message || t('adminJobs.field.logoUploadFailed'), color: 'red' });
        } finally {
            setLogoUploading(false);
        }
    };

    const loadJob = useCallback(async () => {
        try {
            const res = await jobService.adminGet(id);
            const j = res.data || {};
            setForm({
                ...EMPTY,
                ...j,
                totalVacancies: j.totalVacancies ?? '',
                sortOrder: j.sortOrder ?? 0,
                publishedDate: toDate(j.publishedDate),
                deadlineDate: toDate(j.deadlineDate),
                vacancies: (Array.isArray(j.vacancies) ? j.vacancies : []).map((v) => ({ ...v, qualifications: withIds(v.qualifications) })),
                eligibility: withIds(j.eligibility),
                process: Array.isArray(j.process) ? j.process : [],
            });
            setMetadata(j.metadata || null);
            if (j.metadata?.ai) setAiUsage(j.metadata.ai);
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
            navigate('/admin/jobs');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, t]);

    useEffect(() => { if (isEdit) loadJob(); }, [isEdit, loadJob]);

    // Merge AI-extracted fields into the form (shared by all input modes).
    const applyFields = (fields = {}) => {
        setForm((f) => ({
            ...f,
            ...Object.fromEntries(Object.entries(fields).filter(([k]) => k in EMPTY && !['publishedDate', 'deadlineDate'].includes(k))),
            publishedDate: fields.publishedDate ? toDate(fields.publishedDate) : f.publishedDate,
            deadlineDate: fields.deadlineDate ? toDate(fields.deadlineDate) : f.deadlineDate,
            totalVacancies: fields.totalVacancies != null ? fields.totalVacancies : f.totalVacancies,
            vacancies: Array.isArray(fields.vacancies) && fields.vacancies.length ? fields.vacancies.map((v) => ({ ...v, qualifications: withIds(v.qualifications) })) : f.vacancies,
            eligibility: Array.isArray(fields.eligibility) && fields.eligibility.length ? withIds(fields.eligibility) : f.eligibility,
            process: Array.isArray(fields.process) && fields.process.length ? fields.process : f.process,
        }));
    };

    const addFiles = (incoming) => {
        const ok = Array.from(incoming).filter((f) => {
            const n = f.name.toLowerCase();
            return f.type === 'application/pdf' || f.type.startsWith('image/') || f.type.includes('word') || n.endsWith('.docx');
        });
        setFiles((prev) => [...prev, ...ok].slice(0, 6));
    };
    const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

    const handleFetchTeletalk = async () => {
        const limit = parseTeletalkLimit(teletalkLimit);
        setScraping(true);
        setTeletalkResults([]);
        setTeletalkMeta(null);
        try {
            const res = await jobService.importTeletalk(teletalkKeyword.trim(), limit, parseCircular);
            const { jobs = [], total, fetched, limit: appliedLimit, fetchAll, ocr } = res.data || {};
            if (!jobs.length) {
                notifications.show({ message: t('adminJobs.ai.teletalkNoResults'), color: 'red' });
                return;
            }
            setTeletalkResults(jobs.map((j) => ({ ...j, _selected: true })));
            setTeletalkMeta({
                total: total ?? jobs.length,
                fetched: fetched ?? jobs.length,
                limit: appliedLimit,
                fetchAll: fetchAll ?? limit === null,
                ocr: ocr || null,
            });
            notifications.show({ message: t('adminJobs.ai.teletalkFetched', { count: jobs.length }), color: 'green' });
        } catch (error) {
            notifications.show({ title: t('adminJobs.ai.failed'), message: error.message, color: 'red' });
        } finally {
            setScraping(false);
        }
    };

    const toggleTeletalkRow = (i) => setTeletalkResults((rows) => rows.map((r, idx) => (idx === i ? { ...r, _selected: !r._selected } : r)));
    const toggleTeletalkAll = (checked) => setTeletalkResults((rows) => rows.map((r) => ({ ...r, _selected: checked })));

    const useTeletalkRow = (row) => {
        applyFields(row.fields);
        if (row.metadata) setMetadata(row.metadata);
        notifications.show({ message: t('adminJobs.ai.success'), color: 'green' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const importSelected = async () => {
        const chosen = teletalkResults.filter((r) => r._selected);
        if (!chosen.length) { notifications.show({ message: t('adminJobs.ai.teletalkSelectOne'), color: 'red' }); return; }
        const payload = chosen.map((r) => ({
            ...r.fields,
            status: 'draft',
            ...(r.metadata ? { metadata: r.metadata } : {}),
        }));
        setImporting(true);
        try {
            const res = await jobService.bulkCreate(payload);
            const n = res.data?.createdCount ?? payload.length;
            notifications.show({ message: t('adminJobs.ai.teletalkImported', { count: n }), color: 'green' });
            navigate('/admin/jobs');
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        } finally {
            setImporting(false);
        }
    };

    // ── AI: analyse via the active mode (url / upload / text) and fill the form ──
    const handleAnalyze = async () => {
        if (aiMode === 'teletalk') { handleFetchTeletalk(); return; }
        let req;
        let source = null;
        if (aiMode === 'url') {
            const url = scrapeUrl.trim();
            if (!url) { notifications.show({ message: t('adminJobs.ai.urlRequired'), color: 'red' }); return; }
            source = url;
            req = jobService.scrapeUrl(url);
        } else if (aiMode === 'text') {
            const text = pasteText.trim();
            if (text.length < 20) { notifications.show({ message: t('adminJobs.ai.textRequired'), color: 'red' }); return; }
            req = jobService.scrapeText(text);
        } else {
            if (!files.length) { notifications.show({ message: t('adminJobs.ai.filesRequired'), color: 'red' }); return; }
            req = jobService.scrapeFiles(files);
        }

        setScraping(true);
        try {
            const res = await req;
            const { fields = {}, usage } = res.data || {};
            applyFields(fields);
            if (usage) {
                setAiUsage({ ...usage, ...(source ? { scrapedUrl: source } : {}), source: aiMode, scrapedAt: new Date().toISOString() });
            }
            notifications.show({ message: t('adminJobs.ai.success'), color: 'green' });
        } catch (error) {
            notifications.show({ title: t('adminJobs.ai.failed'), message: error.message, color: 'red' });
        } finally {
            setScraping(false);
        }
    };

    // ── Repeatable rows helpers ──
    const addVacancy = () => set('vacancies', [...form.vacancies, { post: '', grade: '', count: '', qualifications: [] }]);
    const updVacancy = (i, k, v) => set('vacancies', form.vacancies.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
    const delVacancy = (i) => set('vacancies', form.vacancies.filter((_, idx) => idx !== i));

    // Per-post (pod) qualifications — nested inside each vacancy row (keyed by _id for drag reordering).
    const setVacQuals = (vi, updater) => set('vacancies', form.vacancies.map((r, idx) => (idx === vi ? { ...r, qualifications: updater(r.qualifications || []) } : r)));
    const addVacQual = (vi) => setVacQuals(vi, (qs) => [...qs, { _id: genId(), key: 'edu', value: '' }]);
    const addVacQualCustom = (vi) => setVacQuals(vi, (qs) => [...qs, { _id: genId(), key: 'custom', label: '', value: '' }]);
    const updVacQual = (vi, id, k, v) => setVacQuals(vi, (qs) => qs.map((q) => (q._id === id ? { ...q, [k]: v } : q)));
    const delVacQual = (vi, id) => setVacQuals(vi, (qs) => qs.filter((q) => q._id !== id));
    const reorderVacQual = (vi, next) => setVacQuals(vi, () => next);

    // Common eligibility — keyed by _id for drag reordering.
    const addElig = () => set('eligibility', [...form.eligibility, { _id: genId(), key: 'edu', value: '' }]);
    const addEligCustom = () => set('eligibility', [...form.eligibility, { _id: genId(), key: 'custom', label: '', value: '' }]);
    const updElig = (id, k, v) => set('eligibility', form.eligibility.map((r) => (r._id === id ? { ...r, [k]: v } : r)));
    const delElig = (id) => set('eligibility', form.eligibility.filter((r) => r._id !== id));
    const reorderElig = (next) => set('eligibility', next);

    const addStep = () => set('process', [...form.process, '']);
    const updStep = (i, v) => set('process', form.process.map((s, idx) => (idx === i ? v : s)));
    const delStep = (i) => set('process', form.process.filter((_, idx) => idx !== i));

    // ── Save ──
    const handleSave = async () => {
        if (!form.organization.trim() || !form.position.trim()) {
            notifications.show({ message: t('adminJobs.requiredFields'), color: 'red' });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                totalVacancies: String(form.totalVacancies ?? '').trim(),
                sortOrder: Number(form.sortOrder) || 0,
                publishedDate: toYmd(form.publishedDate),
                deadlineDate: toYmd(form.deadlineDate),
                vacancies: form.vacancies
                    .filter((r) => r.post || r.grade || r.count)
                    .map((r) => ({ ...stripId(r), qualifications: (r.qualifications || []).filter((q) => q.value).map(stripId) })),
                eligibility: form.eligibility.filter((r) => r.value).map(stripId),
                process: form.process.filter((s) => String(s).trim()),
                metadata: aiUsage ? { ...(metadata || {}), ai: aiUsage } : metadata,
            };
            if (isEdit) {
                await jobService.update(id, payload);
                notifications.show({ message: t('adminJobs.updated'), color: 'green' });
            } else {
                await jobService.create(payload);
                notifications.show({ message: t('adminJobs.created'), color: 'green' });
            }
            navigate('/admin/jobs');
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error.message, color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const opt = (arr, prefix) => arr.map((v) => ({ value: v, label: t(`${prefix}.${v}`) }));

    if (loading) {
        return <div className="p-6 flex items-center justify-center text-zinc-400"><IconLoader2 className="animate-spin mr-2" size={18} /> {t('common.loading')}</div>;
    }

    return (
        <div className="p-3 sm:p-6">
            {/* Admin-only AI cost/debug log — floats top-right, collapsed by default. */}
            <AiUsagePanel usage={aiUsage} />

            <button onClick={() => navigate('/admin/jobs')} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary mb-3">
                <IconArrowLeft size={16} /> {t('adminJobs.backToList')}
            </button>

            <PageHeader title={isEdit ? t('adminJobs.editTitle') : t('adminJobs.createTitle')} />

            <div className="space-y-5 mt-4">
                {/* AI auto-fill panel — URL / Upload / Text */}
                <div className="rounded-xl border border-primary/30 bg-orange-50/60 dark:bg-orange-900/10 p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <IconSparkles size={18} className="text-primary" />
                        <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{t('adminJobs.ai.title')}</h3>
                    </div>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">{t('adminJobs.ai.help')}</p>

                    {/* Mode tabs */}
                    <div className="inline-flex rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-0.5 mb-3">
                        {[
                            { key: 'url', icon: IconLink, label: t('adminJobs.ai.modeUrl') },
                            { key: 'upload', icon: IconUpload, label: t('adminJobs.ai.modeUpload') },
                            { key: 'text', icon: IconClipboardText, label: t('adminJobs.ai.modeText') },
                            { key: 'teletalk', icon: IconApi, label: t('adminJobs.ai.modeTeletalk') },
                        ].map(({ key, icon: Icon, label }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setAiMode(key)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${aiMode === key ? 'bg-primary text-white' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}
                            >
                                <Icon size={14} /> {label}
                            </button>
                        ))}
                    </div>

                    {/* Mode input */}
                    {aiMode === 'url' && (
                        <div className="relative">
                            <IconWorld size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <input
                                type="url"
                                value={scrapeUrl}
                                onChange={(e) => setScrapeUrl(e.target.value)}
                                placeholder={t('adminJobs.ai.urlPlaceholder')}
                                className="w-full pl-9 pr-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                    )}

                    {aiMode === 'upload' && (
                        <div>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full flex flex-col items-center justify-center gap-1.5 px-4 py-6 border-2 border-dashed border-zinc-300 dark:border-zinc-600 rounded-lg text-zinc-500 dark:text-zinc-400 hover:border-primary hover:text-primary transition-colors"
                            >
                                <IconUpload size={22} />
                                <span className="text-sm font-medium">{t('adminJobs.ai.uploadCta')}</span>
                                <span className="text-xs text-zinc-400">{t('adminJobs.ai.uploadHint')}</span>
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,application/pdf,.docx,.doc"
                                multiple
                                hidden
                                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                            />
                            {files.length > 0 && (
                                <div className="mt-2 space-y-1.5">
                                    {files.map((f, i) => (
                                        <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                                            <IconFileText size={16} className="text-zinc-400 flex-shrink-0" />
                                            <span className="flex-1 min-w-0 truncate text-[13px] text-zinc-700 dark:text-zinc-200">{f.name}</span>
                                            <button type="button" onClick={() => removeFile(i)} className="text-zinc-400 hover:text-red-500 flex-shrink-0"><IconX size={15} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {aiMode === 'text' && (
                        <textarea
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                            rows={5}
                            placeholder={t('adminJobs.ai.textPlaceholder')}
                            className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                        />
                    )}

                    {aiMode === 'teletalk' && (
                        <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="relative flex-1">
                                    <IconApi size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                    <input
                                        type="text"
                                        value={teletalkKeyword}
                                        onChange={(e) => setTeletalkKeyword(e.target.value)}
                                        placeholder={t('adminJobs.ai.teletalkKeywordPlaceholder')}
                                        className="w-full pl-9 pr-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                </div>
                                <label className="flex items-center gap-2 sm:w-48">
                                    <span className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                                        {t('adminJobs.ai.teletalkLimit')}
                                    </span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={TELETALK_MAX}
                                        value={teletalkLimit}
                                        placeholder={t('adminJobs.ai.teletalkLimitPlaceholder')}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === '') { setTeletalkLimit(''); return; }
                                            const n = Number(v);
                                            if (!Number.isFinite(n)) return;
                                            setTeletalkLimit(String(Math.min(Math.max(n, 1), TELETALK_MAX)));
                                        }}
                                        className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                </label>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={parseCircular}
                                        onChange={(e) => setParseCircular(e.target.checked)}
                                        className="w-4 h-4 rounded border-zinc-300 text-primary focus:ring-primary/40"
                                    />
                                    <span className="text-xs text-zinc-600 dark:text-zinc-300">{t('adminJobs.ai.teletalkParseCircular')}</span>
                                </label>
                                <p className="text-xs text-zinc-400 dark:text-zinc-500">{t('adminJobs.ai.teletalkHint')}</p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end mt-3">
                        <button
                            onClick={handleAnalyze}
                            disabled={scraping}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold rounded-lg whitespace-nowrap"
                        >
                            {scraping ? <IconLoader2 size={16} className="animate-spin" /> : (aiMode === 'teletalk' ? <IconDownload size={16} /> : <IconSparkles size={16} />)}
                            {scraping
                                ? (aiMode === 'teletalk' ? t('adminJobs.ai.teletalkFetching') : t('adminJobs.ai.analyzing'))
                                : (aiMode === 'teletalk' ? t('adminJobs.ai.teletalkFetch') : t('adminJobs.ai.analyze'))}
                        </button>
                    </div>

                    {aiMode === 'teletalk' && teletalkMeta?.ocr && (
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600 dark:text-zinc-300 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2">
                            <span>
                                <span className="text-zinc-400">{t('adminJobs.ai.teletalkOcrParsed')}:</span>{' '}
                                {teletalkMeta.ocr.parsed}/{teletalkResults.length}
                            </span>
                            {teletalkMeta.ocr.failed > 0 && (
                                <span className="text-amber-600 dark:text-amber-400">
                                    {t('adminJobs.ai.teletalkOcrFailed', { count: teletalkMeta.ocr.failed })}
                                </span>
                            )}
                            <span className="text-green-600 dark:text-green-400 font-medium">{t('adminJobs.ai.teletalkOcrFree')}</span>
                        </div>
                    )}

                    {aiMode === 'teletalk' && teletalkResults.length > 0 && (
                        <div className="mt-4 border-t border-primary/20 pt-3">
                            {teletalkMeta && (
                                <p className="text-xs text-zinc-600 dark:text-zinc-300 mb-2">
                                    {teletalkMeta.fetchAll || teletalkMeta.fetched >= teletalkMeta.total
                                        ? t('adminJobs.ai.teletalkFetchedSummaryAll', { fetched: teletalkMeta.fetched })
                                        : t('adminJobs.ai.teletalkFetchedSummary', {
                                            fetched: teletalkMeta.fetched,
                                            total: teletalkMeta.total,
                                            limit: teletalkMeta.limit,
                                        })}
                                </p>
                            )}
                            <div className="flex items-center justify-between mb-2">
                                <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={teletalkResults.every((r) => r._selected)}
                                        onChange={(e) => toggleTeletalkAll(e.target.checked)}
                                        className="w-4 h-4 rounded border-zinc-300 text-primary focus:ring-primary/40"
                                    />
                                    {t('adminJobs.ai.teletalkSelectAll')}
                                </label>
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                                    {t('adminJobs.ai.teletalkSelectedCount', {
                                        selected: teletalkResults.filter((r) => r._selected).length,
                                        total: teletalkResults.length,
                                    })}
                                </span>
                            </div>

                            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                {teletalkResults.map((row, i) => {
                                    const f = row.fields || {};
                                    const edu = f.eligibility?.find((e) => e.key === 'edu')?.value;
                                    const ocrOk = !!row.metadata?.ocr;
                                    const parseFailed = !!row.metadata?.pdfParseError;
                                    return (
                                        <div key={row.metadata?.teletalk?.id ?? i} className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg border bg-white dark:bg-zinc-800 ${row._selected ? 'border-primary/40' : 'border-zinc-200 dark:border-zinc-700'}`}>
                                            <input
                                                type="checkbox"
                                                checked={row._selected}
                                                onChange={() => toggleTeletalkRow(i)}
                                                className="mt-0.5 w-4 h-4 rounded border-zinc-300 text-primary focus:ring-primary/40 flex-shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[13px] font-semibold text-zinc-800 dark:text-zinc-100 truncate">{f.position || t('adminJobs.ai.teletalkUntitled')}</p>
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                                                    {f.organization || '—'}
                                                    {f.totalVacancies ? ` · ${f.totalVacancies} ${t('adminJobs.field.totalVacancies')}` : ''}
                                                    {f.deadlineDate ? ` · ${f.deadlineDate}` : ''}
                                                    {f.gradeLabel ? ` · ${f.gradeLabel}` : ''}
                                                </p>
                                                {parseCircular && (
                                                    <p className="text-[11px] mt-0.5 truncate">
                                                        {ocrOk && (
                                                            <span className="text-green-600 dark:text-green-400">{t('adminJobs.ai.teletalkRowOcrOk')}</span>
                                                        )}
                                                        {parseFailed && (
                                                            <span className="text-amber-600 dark:text-amber-400" title={row.metadata.pdfParseError}>
                                                                {t('adminJobs.ai.teletalkRowOcrFailed')}
                                                            </span>
                                                        )}
                                                        {!ocrOk && !parseFailed && (
                                                            <span className="text-zinc-400">{t('adminJobs.ai.teletalkRowNoPdf')}</span>
                                                        )}
                                                        {edu && (
                                                            <span className="text-zinc-500 dark:text-zinc-400"> · {edu}</span>
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => useTeletalkRow(row)}
                                                title={t('adminJobs.ai.teletalkUseOne')}
                                                className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                                            >
                                                <IconWand size={13} /> {t('adminJobs.ai.teletalkUseOne')}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    type="button"
                                    onClick={importSelected}
                                    disabled={importing}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold rounded-lg whitespace-nowrap"
                                >
                                    {importing ? <IconLoader2 size={16} className="animate-spin" /> : <IconDownload size={16} />}
                                    {t('adminJobs.ai.teletalkImport', { count: teletalkResults.filter((r) => r._selected).length })}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Basic info */}
                <Card title={t('adminJobs.section.basic')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <TextInput label={t('adminJobs.field.organization')} value={form.organization} onChange={(v) => set('organization', v)} placeholder={t('adminJobs.field.organizationPh')} />
                        <TextInput label={t('adminJobs.field.position')} value={form.position} onChange={(v) => set('position', v)} placeholder={t('adminJobs.field.positionPh')} />
                        <label className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.category')}</span>
                            <SelectField value={form.category} onChange={(v) => set('category', v)} options={opt(CATEGORY_OPTS, 'jobs.categories')} />
                        </label>
                        <TextInput label={t('adminJobs.field.secondaryTag')} value={form.secondaryTag} onChange={(v) => set('secondaryTag', v)} placeholder={t('adminJobs.field.secondaryTagPh')} />
                        <div className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.logo')}</span>
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-full border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
                                    {form.logoUrl ? (
                                        <img src={form.logoUrl} alt="" className="h-full w-full object-contain" />
                                    ) : (
                                        <IconPhoto size={18} className="text-zinc-400" />
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => logoFileRef.current?.click()}
                                    disabled={logoUploading}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-sm rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-60"
                                >
                                    {logoUploading ? <IconLoader2 size={15} className="animate-spin" /> : <IconUpload size={15} />}
                                    {form.logoUrl ? t('adminJobs.field.logoChange') : t('adminJobs.field.logoUpload')}
                                </button>
                                {form.logoUrl && (
                                    <button type="button" onClick={() => set('logoUrl', '')} className="text-sm text-red-500 hover:underline">
                                        {t('biggapon.admin.remove')}
                                    </button>
                                )}
                                <input ref={logoFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoFile} className="hidden" />
                            </div>
                        </div>
                        <TextInput label={t('adminJobs.field.totalVacancies')} value={form.totalVacancies} onChange={(v) => set('totalVacancies', v)} placeholder={t('adminJobs.field.totalVacanciesPh')} />
                        <TextInput label={t('adminJobs.field.gradeLabel')} value={form.gradeLabel} onChange={(v) => set('gradeLabel', v)} placeholder={t('adminJobs.field.gradeLabelPh')} />
                        <TextInput label={t('adminJobs.field.workplace')} value={form.workplace} onChange={(v) => set('workplace', v)} placeholder={t('adminJobs.field.workplacePh')} />
                        <div className="sm:col-span-2">
                            <TextInput label={t('adminJobs.field.posterQualification')} value={form.posterQualification} onChange={(v) => set('posterQualification', v)} placeholder={t('adminJobs.field.posterQualificationPh')} />
                        </div>
                        <div className="sm:col-span-2">
                            <TextInput label={t('adminJobs.field.posterBosoyShima')} value={form.posterBosoyShima} onChange={(v) => set('posterBosoyShima', v)} placeholder={t('adminJobs.field.posterBosoyShimaPh')} />
                        </div>
                    </div>
                </Card>

                {/* Apply + dates */}
                <Card title={t('adminJobs.section.applyDates')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.applyMethod')}</span>
                            <SelectField value={form.applyMethod} onChange={(v) => set('applyMethod', v)} options={opt(APPLY_OPTS, 'adminJobs.applyMethod')} />
                        </label>
                        <TextInput label={t('adminJobs.field.applyMethodLabel')} value={form.applyMethodLabel} onChange={(v) => set('applyMethodLabel', v)} placeholder={t('adminJobs.field.applyMethodLabelPh')} />
                        <label className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.publishedDate')}</span>
                            <DatePickerField value={form.publishedDate} onChange={(d) => set('publishedDate', d)} toYear={new Date().getFullYear() + 2} />
                        </label>
                        <label className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.deadlineDate')}</span>
                            <DatePickerField value={form.deadlineDate} onChange={(d) => set('deadlineDate', d)} toYear={new Date().getFullYear() + 2} />
                        </label>
                        <TextInput label={t('adminJobs.field.feePaymentNote')} value={form.feePaymentNote} onChange={(v) => set('feePaymentNote', v)} placeholder={t('adminJobs.field.feePaymentNotePh')} />
                        <TextInput label={t('adminJobs.field.examDateNote')} value={form.examDateNote} onChange={(v) => set('examDateNote', v)} placeholder={t('adminJobs.field.examDateNotePh')} />
                        <TextInput label={t('adminJobs.field.noticeUrl')} value={form.noticeUrl} onChange={(v) => set('noticeUrl', v)} placeholder="https://…" type="url" />
                        <TextInput label={t('adminJobs.field.applyUrl')} value={form.applyUrl} onChange={(v) => set('applyUrl', v)} placeholder="https://…" type="url" />
                    </div>
                </Card>

                {/* Vacancies (each with its own post-specific qualifications) */}
                <Card title={t('adminJobs.section.vacancies')}>
                    {form.vacancies.map((row, i) => (
                        <div key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-3 space-y-3">
                            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                                <input value={row.post} onChange={(e) => updVacancy(i, 'post', e.target.value)} placeholder={t('adminJobs.vac.post')}
                                    className="flex-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                <input value={row.grade} onChange={(e) => updVacancy(i, 'grade', e.target.value)} placeholder={t('adminJobs.vac.grade')}
                                    className="sm:w-40 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                <input value={row.count} onChange={(e) => updVacancy(i, 'count', e.target.value)} placeholder={t('adminJobs.vac.count')}
                                    className="sm:w-28 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                <button onClick={() => delVacancy(i)} className="p-2 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 self-end sm:self-auto"><IconTrash size={16} /></button>
                            </div>

                            {/* Post-specific qualifications */}
                            <div className="ml-1 border-l-2 border-primary/20 pl-3 space-y-2">
                                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('adminJobs.vac.qualifications')}</p>
                                <QualEditor
                                    rows={row.qualifications || []}
                                    t={t}
                                    opt={opt}
                                    addLabel={t('adminJobs.vac.addQual')}
                                    onReorder={(next) => reorderVacQual(i, next)}
                                    onUpdate={(id, k, v) => updVacQual(i, id, k, v)}
                                    onDelete={(id) => delVacQual(i, id)}
                                    onAdd={() => addVacQual(i)}
                                    onAddCustom={() => addVacQualCustom(i)}
                                />
                            </div>
                        </div>
                    ))}
                    <button onClick={addVacancy} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><IconPlus size={15} /> {t('adminJobs.vac.add')}</button>
                </Card>

                {/* Common eligibility (applies to all posts) */}
                <Card title={t('adminJobs.section.eligibility')}>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 -mt-1">{t('adminJobs.elig.commonHint')}</p>
                    <QualEditor
                        rows={form.eligibility}
                        t={t}
                        opt={opt}
                        addLabel={t('adminJobs.elig.add')}
                        onReorder={reorderElig}
                        onUpdate={updElig}
                        onDelete={delElig}
                        onAdd={addElig}
                        onAddCustom={addEligCustom}
                    />
                </Card>

                {/* Process steps */}
                <Card title={t('adminJobs.section.process')}>
                    {form.process.map((step, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs font-semibold text-zinc-500">{i + 1}</span>
                            <input value={step} onChange={(e) => updStep(i, e.target.value)} placeholder={t('adminJobs.process.step')}
                                className="flex-1 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                            <button onClick={() => delStep(i)} className="p-2 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><IconTrash size={16} /></button>
                        </div>
                    ))}
                    <button onClick={addStep} className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><IconPlus size={15} /> {t('adminJobs.process.add')}</button>
                </Card>

                {/* Publishing */}
                <Card title={t('adminJobs.section.publishing')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">{t('adminJobs.field.status')}</span>
                            <SelectField value={form.status} onChange={(v) => set('status', v)} options={opt(STATUS_OPTS, 'adminJobs.status')} />
                        </label>
                        <TextInput label={t('adminJobs.field.sortOrder')} value={form.sortOrder} onChange={(v) => set('sortOrder', v)} type="number" placeholder="0" />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.isFeatured} onChange={(e) => set('isFeatured', e.target.checked)}
                            className="w-4 h-4 rounded border-zinc-300 text-primary focus:ring-primary/40" />
                        <span className="text-sm text-zinc-700 dark:text-zinc-300">{t('adminJobs.field.isFeatured')}</span>
                    </label>
                </Card>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pb-4">
                    <button onClick={() => navigate('/admin/jobs')}
                        className="px-4 py-2 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 text-sm rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800">
                        {t('common.cancel')}
                    </button>
                    <button onClick={handleSave} disabled={saving}
                        className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold rounded-lg">
                        {saving ? <IconLoader2 size={16} className="animate-spin" /> : <IconDeviceFloppy size={16} />}
                        {isEdit ? t('adminJobs.saveChanges') : t('adminJobs.createJob')}
                    </button>
                </div>
            </div>
        </div>
    );
}
